import React, { useContext } from 'react';
import { AgFinancingDocumentType, AgFinancingFile, ContractStatus, Phase3 } from '@indigo-ag/schema';
import { Body, ColumnDefinition, palettes, RowDefinition, Table, useTableState } from '@indigo-ag/component-module';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMinusCircle, faTimes } from '@fortawesome/pro-regular-svg-icons';

import i18next from 'core/i18n';
import { DocumentCard, documentRequestTypesTranslations, phase3FileTypes, utils } from 'shared';
import { DocumentValidationDetail } from './DocumentValidationDetail/DocumentValidationDetail';
import { UserContext } from 'core/components/GateWay/GateWay';

const validIcon = <FontAwesomeIcon icon={faCheck} color={palettes.brand.green.base} />;
const unprocessedIcon = <FontAwesomeIcon icon={faMinusCircle} color={palettes.brand.gray[600]} />;
const invalidIcon = <FontAwesomeIcon icon={faTimes} color={palettes.brand.red.base} />;

const ALLOWED_CONTRACT_STATUSES = [ContractStatus.REVIEWING_SIGNED_DOCUMENTS, ContractStatus.CREATING_AMENDMENT];

interface DocumentsValidationListProps {
  phase3Info: Phase3;
}

type Phase3FilesColumnId = 'file';
type Phase3FilesDefinitionContext = { readonly: boolean };

const phase3FilesColumnDefinition: ColumnDefinition<
  AgFinancingFile,
  Phase3FilesColumnId,
  Phase3FilesDefinitionContext
>[] = [
  {
    id: 'file',
    header: utils.text.toTitleCase(i18next.t('document')),
    cell: ({ row, SubtitleCell }) => {
      return (
        <SubtitleCell
          label={documentRequestTypesTranslations[row.documentType] || ''}
          sublabel={<DocumentCard document={row} />}
        />
      );
    },
    constraints: {
      min: { rem: 30 },
      max: { fr: 1 }
    }
  }
];

const phase3FilesRowDefinition: RowDefinition<AgFinancingFile, Phase3FilesColumnId, Phase3FilesDefinitionContext> = {
  rowId: ({ row }) => row.id,
  iconCell: {
    ariaLabel: 'Status',
    renderIcon: ({ row }) => {
      switch (row.documentType) {
        case AgFinancingDocumentType.OTHER_VALIDATION:
          if (row.metadata?.phase3FileInfo?.isOtherValidationValid) {
            return validIcon;
          } else if (row.metadata?.phase3FileInfo?.isOtherValidationValid === false) {
            return invalidIcon;
          } else {
            return unprocessedIcon;
          }
        case AgFinancingDocumentType.REGISTERED_LETTER_OF_CONSENT:
          if (row.metadata?.phase3FileInfo?.isConsentLetterValid) {
            return validIcon;
          } else if (row.metadata?.phase3FileInfo?.isConsentLetterValid === false) {
            return invalidIcon;
          } else {
            return unprocessedIcon;
          }
        case AgFinancingDocumentType.REGISTERED_MINUTA:
          if (row.metadata?.phase3FileInfo?.isRegisteredMinutaValid) {
            return validIcon;
          } else if (row.metadata?.phase3FileInfo?.isRegisteredMinutaValid === false) {
            return invalidIcon;
          } else {
            return unprocessedIcon;
          }
        case AgFinancingDocumentType.AMENDMENT_SIGN:
          if (row.metadata?.phase3FileInfo?.isAmendmentSignedValid) {
            return validIcon;
          } else if (row.metadata?.phase3FileInfo?.isAmendmentSignedValid === false) {
            return invalidIcon;
          } else {
            return unprocessedIcon;
          }
        case AgFinancingDocumentType.CERTIFICATE_OF_PLEDGE:
          if (!!row.metadata?.phase3FileInfo?.pledgeCertificateLevel) {
            return validIcon;
          } else {
            return unprocessedIcon;
          }

        default:
          return null;
      }
    }
  },
  expandable: ({ row, ColumnSpan, definitionContext }) => {
    return (
      <ColumnSpan>
        <DocumentValidationDetail file={row} readonly={definitionContext?.readonly} />
      </ColumnSpan>
    );
  }
};

export const DocumentsValidationList: React.FunctionComponent<DocumentsValidationListProps> = ({ phase3Info }) => {
  /**
   * User Context
   */
  const { isLuchesi } = useContext(UserContext);

  const phase3Files: AgFinancingFile[] = phase3Info?.files?.filter(f => phase3FileTypes.includes(f.documentType)) || [];
  const tableState = useTableState<AgFinancingFile, Phase3FilesColumnId>(phase3Files);

  const isContractStatusValid =
    !!phase3Info.contractStatus && !ALLOWED_CONTRACT_STATUSES.includes(phase3Info.contractStatus);

  return phase3Files.length ? (
    <Table
      label="Documents"
      columnDefinition={phase3FilesColumnDefinition}
      rowDefinition={phase3FilesRowDefinition}
      definitionContext={{
        readonly: !isLuchesi || isContractStatusValid
      }}
      {...tableState}
    />
  ) : (
    <Body>{utils.text.firstLetterUppercase(i18next.t('noSignedDocumentsMessage'))}</Body>
  );
};
